/**
 * @class
 * @memberOf P3.math
 */
export default class Vector2 {
    /**
     * @param {number} [x=0] - The x axis of the vector.
     * @param {number} [y=0] - The y axis of the vector.
     */
    constructor(x = 0, y = 0) {
        /**
         * @member {number}
         */
        this.x = x;

        /**
         * @member {number}
         */
        this.y = y;
    }

    /**
     * Returns a new vector equal to the sum of the vector plus the given vector.
     *
     * @param {Vector2} v - The vector to add.
     * @returns {Vector2}
     */
    add(v) {
        return new Vector2(this.x + v.x, this.y + v.y);
    }

    /**
     * Returns a new vector equal to the sum of the vector minus the given vector.
     *
     * @param {Vector2} v - The vector to subtract.
     * @returns {Vector2}
     */
    subtract(v) {
        return new Vector2(this.x - v.x, this.y - v.y);
    }

    /**
     * Returns a new vector equal to the sum of the vector scaled by the given value.
     *
     * @param {Vector2} s - The amount to scale the vector by.
     * @returns {Vector2}
     */
    scale(s) {
        return new Vector2(this.x * s, this.y * s);
    }

    /**
     * Increments this vector by the given vector.
     *
     * @param {Vector2} v - The vector to add.
     * @returns {Vector2}
     */
    incrementBy(v) {
        this.x += v.x;
        this.y += v.y;
        return this;
    }

    /**
     * Decrements this vector by the given vector.
     *
     * @param {Vector2} v - The vector to subtract.
     * @returns {Vector2}
     */
    decrementBy(v) {
        this.x -= v.x;
        this.y -= v.y;
        return this;
    }

    /**
     * Scale this vector by the given value.
     * @param {number} s - The amount to scale the vector by.
     * @returns {Vector2}
     */
    scaleBy(s) {
        this.x *= s;
        this.y *= s;
        return this;
    }

    /**
     * Normalize the vector to a length of 1.
     * @returns {Vector2}
     */
    normalize() {
        const l = this.length;
        if (l > 0) {
            this.x /= l;
            this.y /= l;
        }
        return this;
    }

    /**
     * Crop the vector to the given length.
     *
     * @param {number} length - The length the vector must exceed.
     * @returns {Vector2}
     */
    truncate(length) {
        const l = this.length;
        if (l > length) {
            this.x /= l * length;
            this.y /= l * length;
        }
        return this;
    }

    /**
     * Returns the left hand normal of this vector.
     *
     * @returns {Vector2}
     */
    normal() {
        return new Vector2(-this.y, this.x);
    }

    /**
     * Returns the dot product for this and the given vector.
     *
     * @param {Vector2} v - The other vector in the equation.
     * @returns {number}
     */
    dot(v) {
        return this.x * v.x + this.y * v.y;
    }

    /**
     * Returns the dot product for the left hand normal of this vector and the given vector.
     *
     * @param {Vector2} v - The other vector in the equation.
     * @returns {number}
     */
    perp(v) {
        return -this.y * v.x + this.x * v.y;
    }

    /**
     * Returns the angle between this vector and the given vector.
     *
     * @param {Vector2} v - The other vector in the equation.
     * @returns {number}
     */
    angleBetween(v) {
        return Math.atan2(v.y - this.y, v.x - this.x);
    }

    /**
     * Rotate the vector.
     *
     * @param {number} angle - The angle in radians to rotate the vector by.
     * @returns {Vector2}
     */
    rotate(angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);

        const x = cos * this.x - sin * this.y;
        const y = cos * this.y + sin * this.x;

        this.x = x;
        this.y = y;

        return this;
    }

    /**
     * Rotate the vector around a point.
     *
     * @param {number} x - The x axis to rotate around.
     * @param {number} y - The y axis to rotate around.
     * @param {number} angle - The angle in radians to rotate the Vector2 by.
     * @returns {Vector2}
     */
    rotateAround(x, y, angle) {
        this.x -= x;
        this.y -= y;

        this.rotate(angle);

        this.x += x;
        this.y += y;

        return this;
    }

    /**
     * Creates a clone of the vector.
     *
     * @returns {Vector2}
     */
    clone() {
        return new Vector2(this.x, this.y);
    }

    /**
     * Copies x and y from the given vector.
     *
     * @param {Vector2} v - The vector to copy.
     */
    copy(v) {
        this.set(v.x, v.y);
    }

    /**
     * Returns true if the given vector is equal to the given vector.
     *
     * @param {Vector2} v - The vector to compare with.
     * @returns {boolean}
     */
    equals(v) {
        return (v.x === this.x) && (v.x === this.y);
    }

    /**
     * Sets the vector to a new x and y axis.
     * If the y is omitted, both x and y will be set to x.
     *
     * @param {number} [x=0]
     * @param {number} [y=0]
     */
    set(x, y) {
        this.x = x || 0;
        this.y = y || ((y !== 0) ? this.x : 0);
    }

    /**
     * Returns a unit vector.
     *
     * @returns {Vector2}
     */
    unit() {
        const v = this.clone();
        v.normalize();
        return v;
    }

    /**
     * Return the angle (in radians) of the vector.
     *
     * @returns {number}
     */
    get angle() {
        return Math.atan2(this.y, this.x);
    }

    /**
     * Returns the magnitude of the vector.
     *
     * @returns {number}
     */
    get length() {
        return Math.sqrt(this.lengthSq);
    }

    /**
     * Returns the magnitude squared of the vector.
     * This is significantly faster than getting the length.
     *
     * @returns {number}
     */
    get lengthSq() {
        return this.x * this.x + this.y * this.y;
    }
}
